/**
 * Sequences domain — React Query hooks.
 *
 * The per-campaign read is a hand-written wrapper because the list endpoint
 * returns an array of at most one sequence, which we unwrap to `Sequence |
 * null` under the canonical `sequenceByCampaignKey`. Write hooks are wrapped
 * via `useInvalidatingMutation` so invalidation is declarative and enforced by
 * the `SequencesMutationId` registry in `./invalidation`.
 *
 * Write mutations that need campaign-scoped invalidation take the lonesome
 * `campaign_id` as an extra variable (it isn't part of the request body for
 * update/delete/step actions) so the registry can invalidate the right caches.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { listSequences } from '../generated/clients/listSequences'
import { createSequence } from '../generated/clients/createSequence'
import { updateSequence } from '../generated/clients/updateSequence'
import { deleteSequence } from '../generated/clients/deleteSequence'
import { setSequenceStepActive } from '../generated/clients/setSequenceStepActive'
import { sendSequenceStepTestEmail } from '../generated/clients/sendSequenceStepTestEmail'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import { sequenceByCampaignKey, type Sequence } from './utils'

import type { CreateSequenceMutationRequest } from '../generated/types/CreateSequence'
import type {
  UpdateSequenceMutationRequest,
  UpdateSequencePathParams,
} from '../generated/types/UpdateSequence'
import type { DeleteSequencePathParams } from '../generated/types/DeleteSequence'
import type {
  SetSequenceStepActiveMutationRequest,
  SetSequenceStepActivePathParams,
} from '../generated/types/SetSequenceStepActive'
import type {
  SendSequenceStepTestEmailMutationRequest,
  SendSequenceStepTestEmailPathParams,
} from '../generated/types/SendSequenceStepTestEmail'

// ── Generated read-hook re-exports (detail by id) ───────────────────

export {
  useGetSequence,
  getSequenceQueryKey,
} from '../generated/hooks/sequencesController'

// ── Read: the sequence for a campaign ───────────────────────────────

/**
 * Hand-written wrapper around the generated `listSequences` client. The list
 * endpoint returns `[]` or `[sequence]`; this unwraps to `Sequence | null` and
 * caches it under the canonical per-campaign key.
 */
export function useGetCampaignSequence(
  campaignId: number,
  enabled = true,
  options?: {
    query?: Omit<
      UseQueryOptions<Sequence | null, Error>,
      'queryKey' | 'queryFn' | 'enabled'
    >
  }
) {
  return useQuery<Sequence | null, Error>({
    queryKey: sequenceByCampaignKey(campaignId),
    queryFn: async () => {
      const sequences = await listSequences({ campaign_id: campaignId })
      return sequences.length > 0 ? sequences[0] : null
    },
    enabled: enabled && campaignId > 0,
    ...options?.query,
  })
}

// ── Write hooks ─────────────────────────────────────────────────────

export function useCreateSequence() {
  return useInvalidatingMutation('createSequence', invalidationRegistry, {
    mutationFn: (variables: { data: CreateSequenceMutationRequest }) =>
      createSequence(variables.data),
  })
}

export function useUpdateSequence() {
  return useInvalidatingMutation('updateSequence', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateSequencePathParams['id']
      campaign_id: number
      data: UpdateSequenceMutationRequest
    }) => updateSequence({ id: variables.id }, variables.data),
  })
}

export function useDeleteSequence() {
  return useInvalidatingMutation('deleteSequence', invalidationRegistry, {
    mutationFn: (variables: {
      id: DeleteSequencePathParams['id']
      campaign_id: number
    }) => deleteSequence({ id: variables.id }),
  })
}

export function useSetSequenceStepActive() {
  return useInvalidatingMutation(
    'setSequenceStepActive',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        bison_step_id: SetSequenceStepActivePathParams['bison_step_id']
        campaign_id: number
        data: SetSequenceStepActiveMutationRequest
      }) =>
        setSequenceStepActive(
          { bison_step_id: variables.bison_step_id },
          variables.data
        ),
    }
  )
}

export function useSendSequenceStepTestEmail() {
  return useInvalidatingMutation(
    'sendSequenceStepTestEmail',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        bison_step_id: SendSequenceStepTestEmailPathParams['bison_step_id']
        data: SendSequenceStepTestEmailMutationRequest
      }) =>
        sendSequenceStepTestEmail(
          { bison_step_id: variables.bison_step_id },
          variables.data
        ),
    }
  )
}

// ── Type re-exports for consumers ───────────────────────────────────

export type { ListSequencesQueryParams } from '../generated/types/ListSequences'
export type { CreateSequenceMutationRequest } from '../generated/types/CreateSequence'
export type {
  UpdateSequenceMutationRequest,
  UpdateSequencePathParams,
} from '../generated/types/UpdateSequence'
export type { DeleteSequencePathParams } from '../generated/types/DeleteSequence'
export type {
  SetSequenceStepActiveMutationRequest,
  SetSequenceStepActivePathParams,
} from '../generated/types/SetSequenceStepActive'
export type {
  SendSequenceStepTestEmailMutationRequest,
  SendSequenceStepTestEmailPathParams,
} from '../generated/types/SendSequenceStepTestEmail'
